import {DateTime} from 'luxon';
import {createParser, useQueryStates} from 'nuqs';
import {useCallback, useEffect, useMemo} from 'react';

const parseDateTime = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const dt = DateTime.fromISO(value, {zone: 'utc'});
  return dt.isValid ? dt.toISO() : null;
};

const isoStringParser = createParser({
  parse: value => parseDateTime(value),
  serialize: value => value || '',
});

const useDateTimeParams = () => {
  const [{dateTimeFrom, dateTimeTo}, setDateTime] = useQueryStates(
    {
      dateTimeFrom: isoStringParser,
      dateTimeTo: isoStringParser,
    },
    {
      urlKeys: {
        dateTimeFrom: 'from',
        dateTimeTo: 'to',
      },
    },
  );

  useEffect(() => {
    if (dateTimeFrom === null && dateTimeTo === null) {
      setDateTime({
        dateTimeFrom: DateTime.now().startOf('day').toISO(),
        dateTimeTo: DateTime.now().endOf('day').toISO(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {parsedDateTimeFrom, parsedDateTimeTo} = useMemo(() => {
    if (!dateTimeFrom || !dateTimeTo) {
      return {parsedDateTimeFrom: null, parsedDateTimeTo: null};
    }

    const parsedDateTimeFrom = DateTime.fromISO(dateTimeFrom);
    const parsedDateTimeTo = DateTime.fromISO(dateTimeTo);

    if (!parsedDateTimeFrom.isValid || !parsedDateTimeTo.isValid) {
      return {parsedDateTimeFrom: null, parsedDateTimeTo: null};
    }

    return {parsedDateTimeFrom, parsedDateTimeTo};
  }, [dateTimeFrom, dateTimeTo]);

  const setDateTimeParams = useCallback(
    ({
      dateTimeFrom,
      dateTimeTo,
    }: {
      dateTimeFrom: string | null;
      dateTimeTo: string | null;
    }) => {
      setDateTime(() => ({
        dateTimeFrom: parseDateTime(dateTimeFrom),
        dateTimeTo: parseDateTime(dateTimeTo),
      }));
    },
    [setDateTime],
  );

  const resetDateTimeParams = useCallback(() => {
    setDateTimeParams({
      dateTimeFrom: null,
      dateTimeTo: null,
    });
  }, [setDateTimeParams]);

  return {
    dateTimeFrom,
    dateTimeTo,
    parsedDateTimeFrom,
    parsedDateTimeTo,
    resetDateTimeParams,
    setDateTimeParams,
  };
};

export default useDateTimeParams;
